/**
 * Deterministic strategy graph builder.
 *
 * Takes a compact Recipe (array of RecipeStep) and produces a full
 * { nodes, edges } graph matching the shape expected by setNodesAndEdges().
 *
 * Pure function — no React dependencies, no side effects. Node
 * materialization (defaults, discriminated unions, OHLCV sugar) lives in
 * graph-engine/materialize.ts, shared with the AI graph engine's edit ops.
 */

import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import type { UniversalNodeSchema } from '@/features/backtest/schemas/universal-node-schema'
import type { GraphNode, GraphEdge } from '@/features/backtest/schemas/backtest-types'
import type { Recipe, RecipeStep, RecipeBuildOutput } from './recipe-types'
import { materializeNode } from '@/features/backtest/graph-engine/materialize'
import type { EngineError } from '@/features/backtest/graph-engine/types'
import { withCustomName } from '@/features/backtest/utils/node-display-name'

// ── Layout constants (match existing template patterns) ──────

const COLUMN_GAP = 250
const ROW_GAP = 150
const START_X = 50
const START_Y = 100

// ── Public API ───────────────────────────────────────────────

export function buildGraphFromRecipe(recipe: Recipe): RecipeBuildOutput {
  const engineErrors: EngineError[] = []

  // Phase 1: Validate structure
  if (!recipe || recipe.length === 0) {
    return { success: false, errors: ['Recipe must be a non-empty array'] }
  }

  const idSet = new Set<string>()
  for (const step of recipe) {
    if (!step.id) {
      engineErrors.push({ code: 'INVALID_REF', message: 'Every step must have an id' })
      continue
    }
    if (idSet.has(step.id)) {
      engineErrors.push({
        code: 'DUPLICATE_STEP_ID',
        stepId: step.id,
        message: `Duplicate step id: "${step.id}"`,
      })
    }
    idSet.add(step.id)

    if (!UniversalNodeRegistry.hasSchema(step.type)) {
      engineErrors.push({
        code: 'UNKNOWN_TYPE',
        stepId: step.id,
        message: `Step "${step.id}": unknown node type "${step.type}"`,
        suggestion: `Available: ${UniversalNodeRegistry.getRegisteredTypes().join(', ')}`,
      })
    }

    // Validate input references point to known step IDs (string refs only —
    // number/boolean literals materialize as static inputs)
    if (step.inputs) {
      for (const [handle, ref] of Object.entries(step.inputs)) {
        if (typeof ref !== 'string') continue
        const sourceId = ref.split('.')[0]
        if (!recipe.some((s) => s.id === sourceId)) {
          engineErrors.push({
            code: 'UNKNOWN_STEP',
            stepId: step.id,
            handle,
            message: `Step "${step.id}": input "${handle}" references unknown node "${sourceId}"`,
          })
        }
      }
    }
  }

  if (engineErrors.length > 0) {
    return toBuildError(engineErrors)
  }

  // Phase 2: Topological sort
  const sorted = topologicalSort(recipe)
  if (!sorted) {
    return toBuildError([{ code: 'CYCLE', message: 'Recipe contains a cycle' }])
  }

  // Phase 3 & 4: Materialize nodes and edges
  const schemaById = new Map<string, UniversalNodeSchema>()
  for (const step of sorted) {
    schemaById.set(step.id, UniversalNodeRegistry.getSchema(step.type)!)
  }

  const depths = computeDepths(sorted)
  const positions = layoutNodes(sorted, depths)

  const nodes: GraphNode[] = []
  const edges: GraphEdge[] = []
  let priceDataCount = 0

  for (const step of sorted) {
    if (step.type === 'priceData') priceDataCount++

    const result = materializeNode(step, {
      schemaById,
      position: positions.get(step.id)!,
      isPrimary: step.type === 'priceData' ? priceDataCount === 1 : undefined,
    })

    engineErrors.push(...result.errors)
    // A named step (code mode's `// @name`) is a renamed node.
    nodes.push(
      step.name
        ? { ...result.node, data: withCustomName(result.node.data ?? {}, step.name) }
        : result.node,
    )
    edges.push(...result.edges)
  }

  if (engineErrors.length > 0) {
    return toBuildError(engineErrors)
  }

  return { success: true, nodes, edges }
}

function toBuildError(engineErrors: EngineError[]): RecipeBuildOutput {
  return {
    success: false,
    errors: engineErrors.map((e) => e.message + (e.suggestion ? `. ${e.suggestion}` : '')),
    engineErrors,
  }
}

// ── Topological sort (Kahn's algorithm) ─────────────────────

function topologicalSort(recipe: Recipe): RecipeStep[] | null {
  const idToStep = new Map<string, RecipeStep>()
  const inDegree = new Map<string, number>()
  const adjacency = new Map<string, string[]>()

  for (const step of recipe) {
    idToStep.set(step.id, step)
    inDegree.set(step.id, 0)
    adjacency.set(step.id, [])
  }

  // Build edges from inputs (string refs only)
  for (const step of recipe) {
    if (!step.inputs) continue
    const deps = new Set<string>()
    for (const ref of Object.values(step.inputs)) {
      if (typeof ref !== 'string') continue
      deps.add(ref.split('.')[0])
    }
    for (const dep of deps) {
      adjacency.get(dep)!.push(step.id)
      inDegree.set(step.id, (inDegree.get(step.id) ?? 0) + 1)
    }
  }

  // BFS from zero-degree nodes
  const queue: string[] = []
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id)
  }

  const sorted: RecipeStep[] = []
  while (queue.length > 0) {
    const id = queue.shift()!
    sorted.push(idToStep.get(id)!)
    for (const neighbor of adjacency.get(id) ?? []) {
      const newDeg = (inDegree.get(neighbor) ?? 1) - 1
      inDegree.set(neighbor, newDeg)
      if (newDeg === 0) queue.push(neighbor)
    }
  }

  return sorted.length === recipe.length ? sorted : null
}

// ── Depth computation for layout ────────────────────────────

function computeDepths(sorted: RecipeStep[]): Map<string, number> {
  const depths = new Map<string, number>()

  for (const step of sorted) {
    let maxParentDepth = -1
    if (step.inputs) {
      for (const ref of Object.values(step.inputs)) {
        if (typeof ref !== 'string') continue
        const sourceId = ref.split('.')[0]
        const parentDepth = depths.get(sourceId) ?? 0
        if (parentDepth > maxParentDepth) maxParentDepth = parentDepth
      }
    }
    depths.set(step.id, maxParentDepth + 1)
  }

  return depths
}

function layoutNodes(
  sorted: RecipeStep[],
  depths: Map<string, number>,
): Map<string, { x: number; y: number }> {
  // Group by depth (column)
  const columns = new Map<number, string[]>()
  for (const step of sorted) {
    const d = depths.get(step.id)!
    if (!columns.has(d)) columns.set(d, [])
    columns.get(d)!.push(step.id)
  }

  const positions = new Map<string, { x: number; y: number }>()
  for (const [depth, ids] of columns) {
    const x = START_X + depth * COLUMN_GAP
    const totalHeight = (ids.length - 1) * ROW_GAP
    const startY = START_Y + (ids.length > 1 ? -totalHeight / 2 : 0)
    for (let i = 0; i < ids.length; i++) {
      positions.set(ids[i], { x, y: startY + i * ROW_GAP })
    }
  }

  return positions
}
